import { Link, type LinkProps, useRouterState } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useOverflowEdges } from "@/lib/hooks";
import { cn } from "@/lib/utils";

export interface SectionNavItem {
  to: LinkProps["to"];
  label: string;
  /** Decorative icon before the label. */
  icon?: LucideIcon;
  /** Only the exact path counts as current (for overview entries). */
  exact?: boolean;
  /** Number shown as a badge when greater than zero. */
  badge?: number;
  /** Accessible name of the badge, e.g. "2 pending uploads". */
  badgeLabel?: string;
}

/**
 * Sub-navigation of a section: a vertical sidebar from `md` up (`sidebar`) or a row of tabs (`tabs`).
 * Below `md` both variants are a horizontally scrollable row without a visible scrollbar: an edge
 * fade (`data-overflow-start` / `data-overflow-end`) shows that more entries exist, and the current
 * entry is scrolled into view.
 */
export function SectionNav({
  items,
  label,
  variant,
}: {
  items: SectionNavItem[];
  label: string;
  variant: "sidebar" | "tabs";
}) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { ref, props } = useOverflowEdges<HTMLElement>(items.length, pathname);

  return (
    <nav
      ref={ref}
      aria-label={label}
      data-variant={variant}
      {...props}
      className={cn(
        "flex gap-1 overflow-x-auto [mask-image:var(--fade)] [-webkit-mask-image:var(--fade)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        variant === "sidebar"
          ? "md:flex-col md:overflow-visible md:[-webkit-mask-image:none] md:[mask-image:none]"
          : "border-b",
      )}
    >
      {items.map((item) => (
        <Link
          key={item.to as string}
          to={item.to}
          activeOptions={{ exact: item.exact ?? false, includeSearch: false }}
          className={cn(
            "inline-flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap hover:bg-accent focus-visible:ring-ring outline-none focus-visible:ring-2",
            "data-[status=active]:bg-primary-subtle data-[status=active]:text-primary-subtle-foreground data-[status=active]:ring-1 data-[status=active]:ring-primary/60 data-[status=active]:ring-inset",
            variant === "tabs" && "rounded-b-none",
          )}
        >
          {item.icon ? <item.icon className="size-4 shrink-0" aria-hidden /> : null}
          {item.label}
          {item.badge && item.badge > 0 ? (
            <Badge variant="secondary" aria-label={item.badgeLabel}>
              {item.badge}
            </Badge>
          ) : null}
        </Link>
      ))}
    </nav>
  );
}
